/// <amd-module name="VSS/Features/PlatformUI/Components/ContributedPillGroup/ContributedPillGroup" />
import * as React from "react";
import { IReadonlyObservableArray, ObservableCollection } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { traceWarning } from "VSS/Platform/Trace";
import { Observer } from "VSSUI/Observer";
import { IPillProps, Pill, PillVariant } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import { Tooltip } from "VSSUI/TooltipEx";
import { getContributedItems, wrapExternalObservable } from "../../ContributedItemUtil";
import { contributedIconToIconProps } from "../ContributedIcon/ContributedIcon";
import { IContributedPill, IContributedPillGroupProps } from "./ContributedPillGroup.Props";

export interface IPillProviderService<T> {
    getPills: (
        pillGroupId: string,
        context?: T
    ) => Promise<IContributedPill[] | IReadonlyObservableArray<IContributedPill>> | IReadonlyObservableArray<IContributedPill> | undefined;
}

function addPillsFromService<T>(
    pageContext: IVssPageContext,
    collection: ObservableCollection<IContributedPill>,
    pillService: IPillProviderService<T>,
    pillGroupId: string,
    contributionId: string,
    context?: T
): void {
    const pillsResult = pillService.getPills(pillGroupId, context);
    const pillsPromise = pillsResult as Promise<IContributedPill[] | IReadonlyObservableArray<IContributedPill>>;
    const pillsArray = pillsResult as IReadonlyObservableArray<IContributedPill>;

    if (pillsPromise && typeof pillsPromise.then === "function") {
        pillsPromise.then(
            resolvedItems => {
                if (resolvedItems) {
                    collection.push(wrapExternalObservable(resolvedItems), contributedPill => {
                        return { ...contributedPill, contributionId };
                    });
                }
            },
            (e: Error) => {
                const message = `Failed to load pills for ${pillGroupId}: ${JSON.stringify(e || "")}`;
                traceWarning(pageContext, "extensions", "ContributedPillGroup", message, { pillGroupId });
                console.warn(message);
            }
        );
    } else if (pillsArray) {
        collection.push(pillsArray, contributedPill => {
            return { ...contributedPill, contributionId };
        });
    }
}

/**
 * Gets an observable array of pills for the specified pill group
 *
 * @param pageContext Page context
 * @param pillGroupId Contribution id of the pill group
 * @param context Optional context to pass to the pill provider service
 */
export function getPills<T>(pageContext: IVssPageContext, pillGroupId: string, context?: T): IReadonlyObservableArray<IContributedPill> {
    const collection = new ObservableCollection<IContributedPill>();
    addPills<T>(pageContext, collection, pillGroupId, context);
    return collection;
}

/**
 * Adds contributed pills targeting the specified pill group to the given collection of pills
 *
 * @param pageContext Page context
 * @param collection Collection of pills to add to
 * @param pillGroupId Contribution id of the pill group
 * @param context Optional context to pass to the pill provider service
 */
export function addPills<T>(
    pageContext: IVssPageContext,
    collection: ObservableCollection<IContributedPill>,
    pillGroupId: string,
    context?: T
): void {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

    const dynamicProviders = getContributedItems(pageContext, pillGroupId, "pillGroupId", "ms.vss-web.pill-provider");
    for (const dynamicProvider of dynamicProviders) {
        const pillService = contributionService.getServiceSync<IPillProviderService<T>>(dynamicProvider.id);
        if (pillService) {
            addPillsFromService(pageContext, collection, pillService, pillGroupId, dynamicProvider.id, context);
        } else {
            contributionService.getService<IPillProviderService<T>>(dynamicProvider.id).then(pillService => {
                if (pillService) {
                    addPillsFromService(pageContext, collection, pillService, pillGroupId, dynamicProvider.id, context);
                }
            });
        }
    }
}

function contributedPillToPillProps(pageContext: IVssPageContext, contributedPill: IContributedPill): IPillProps & { children?: React.ReactNode } {
    return {
        children: (
            <Tooltip text={"" + (contributedPill.tooltip || contributedPill.text)} overflowOnly={!contributedPill.tooltip}>
                <span>{"" + contributedPill.text}</span>
            </Tooltip>
        ),
        color: contributedPill.color,
        iconProps: contributedIconToIconProps(pageContext, contributedPill.contributionId, contributedPill.icon),
        onClick: contributedPill.onClick ? () => contributedPill.onClick!() : undefined,
        onRemoveClick: contributedPill.onClick ? () => contributedPill.onRemoveClick!() : undefined,
        variant: contributedPill.color ? PillVariant.colored : PillVariant.standard
    };
}

/**
 * Renders a group of pills based off contributions
 */
export function ContributedPillGroup(props: IContributedPillGroupProps): JSX.Element | null {
    const { className, overflow, pageContext, pills, size } = props;
    return (
        <Observer pills={pills}>
            {(pillProps: { pills: IContributedPill[] }) => {
                return pillProps.pills.length ? (
                    <PillGroup className={className} overflow={overflow}>
                        {pillProps.pills.map((pill, index) => (
                            <Pill {...contributedPillToPillProps(pageContext, pill)} key={index} size={size} />
                        ))}
                    </PillGroup>
                ) : null;
            }}
        </Observer>
    );
}
